import { Injectable } from '@angular/core';
import { PersonalizedContentFacade } from '@store/personalized-content/personalized-content.facade';
import { zipObj } from 'ramda';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  getCMSContentHandler,
  LookupTable,
} from './personalized-content.parser';
import {
  DynamicallyEvaluated,
  SupportsDynamicExpressions,
} from './types/personalized-content.type';

@Injectable({ providedIn: 'root' })
export class PersonalizedContentService {
  constructor(private personalizedContentFacade: PersonalizedContentFacade) {}

  initializeDataWithDynamicExpression<U>(
    data: SupportsDynamicExpressions<U>,
  ): DynamicallyEvaluated<U> {
    const evaluationResult = (data.dynamicExpressions || []).reduce(
      (acc, { target, expression }) => ({
        ...acc,
        [target]: this.evaluateExpression(expression),
      }),
      {},
    );
    return { ...data, evaluationResult } as any;
  }

  evaluateExpression(dynamicExpression: string): Observable<string | null> {
    try {
      const handler = getCMSContentHandler(dynamicExpression);

      const dependencies = handler.next();
      /*
        Typescript needs --strictNullChecks to properly discriminate the tagged union. Will have to use
        a === for the time being.
      */
      // eslint-disable-next-line
      if (dependencies.done === false) {
        const keys = dependencies.value;
        const assignContent = (lookupTable: Record<string, string>) =>
          this.assignContent(handler, lookupTable);

        if (keys.length > 0) {
          const observables = keys.map(
            this.personalizedContentFacade.getPersonalizedContentWithKey,
          );

          return combineLatest(observables).pipe(
            map((results: string[]) => zipObj(keys, results)),
            map(result => assignContent(result)),
            catchError(() => of(null)),
          );
        } else {
          return of(assignContent({})).pipe(catchError(() => of(null)));
        }
      }
    } catch (error) {
      // Failed to parse ; return null.
    }

    return of(null);
  }

  assignContent(
    handler: ReturnType<typeof getCMSContentHandler>,
    lookupTable: LookupTable,
  ) {
    const result = handler.next(lookupTable);

    if (result.done) {
      return result.value;
    }
    return null;
  }
}
